import manifestMeta from "../data/learning-manifest-meta.json";
import { analectById, gateById, getRecord } from "../engine";
import type { GateId } from "../engine";
import type { GameState } from "../state/duelStore";
import { getSession, recordEvent, SITE_KEY, syncProgress } from "./identity";
import type { BdfzSession, SyncProgressPayload, SyncRecordPayload } from "./identity";

export interface LearningUnitCompletion {
  resourceKey: string;
  quoteId: number;
  gate: GateId;
  correct: boolean;
  attemptNumber: number;
  submittedAt: number;
  eventNonce: string;
}

export interface EvidencePublication {
  status: "anonymous" | "published";
  resourceKey: string;
  manifestVersion: string;
}

interface PublicationDependencies {
  getSessionImpl?: () => Promise<BdfzSession>;
  recordSourceCompletionImpl?: (completion: LearningUnitCompletion) => Promise<SourceCompletionReceipt>;
  syncProgressImpl?: (payload: SyncProgressPayload) => Promise<void>;
  recordEventImpl?: (payload: SyncRecordPayload) => Promise<void>;
  sourceUrl?: string;
}

interface SourceCompletionReceipt {
  ok: true;
  sourceSiteKey: "ly";
  resourceKey: string;
  manifestVersion: string;
  resourceVersion: string;
  recorded: boolean;
}

export const LEARNING_MANIFEST_VERSION = manifestMeta.manifestVersion;
export const LEARNING_MANIFEST_ITEM_COUNT = manifestMeta.itemCount;

export function learningResourceKey(quoteId: number, gate: GateId): string {
  return `ly:q${String(quoteId).padStart(4, "0")}:${gate}`;
}

export function learningUnitCompletionForAnswer(
  state: GameState,
  optionId: string,
  submittedAt = Date.now(),
): LearningUnitCompletion | null {
  const { duel } = state;
  const challenge = duel.challenge;
  if (!challenge || duel.selected || optionId === duel.hintedOut) return null;
  if (!challenge.options.some((option) => option.id === optionId)) return null;

  const quoteId = duel.analectId;
  const gate = challenge.gate;
  const previousGate = getRecord(state.progress, quoteId).gates[gate];
  return {
    resourceKey: learningResourceKey(quoteId, gate),
    quoteId,
    gate,
    correct: optionId === challenge.answerId,
    attemptNumber: previousGate.attempts + 1,
    submittedAt,
    eventNonce: globalThis.crypto?.randomUUID?.()
      ?? `ly-${quoteId}-${gate}-${submittedAt}-${previousGate.attempts + 1}`,
  };
}

async function recordSourceCompletion(completion: LearningUnitCompletion): Promise<SourceCompletionReceipt> {
  const response = await fetch("/api/learning/complete", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      resourceKey: completion.resourceKey,
      manifestVersion: LEARNING_MANIFEST_VERSION,
      eventNonce: completion.eventNonce,
      completionKind: "answer_submitted",
    }),
  });
  const payload = await response.json().catch(() => ({})) as Partial<SourceCompletionReceipt> & { error?: string };
  if (!response.ok) throw new Error(payload.error || `source completion ${response.status}`);
  if (payload.ok !== true
    || payload.sourceSiteKey !== SITE_KEY
    || payload.resourceKey !== completion.resourceKey
    || payload.manifestVersion !== LEARNING_MANIFEST_VERSION
    || typeof payload.resourceVersion !== "string") {
    throw new Error("source completion receipt mismatch");
  }
  return payload as SourceCompletionReceipt;
}

export async function publishLearningUnitCompletion(
  completion: LearningUnitCompletion,
  dependencies: PublicationDependencies = {},
): Promise<EvidencePublication> {
  const getSessionImpl = dependencies.getSessionImpl ?? getSession;
  const session = await getSessionImpl();
  if (!session.authenticated) {
    return {
      status: "anonymous",
      resourceKey: completion.resourceKey,
      manifestVersion: LEARNING_MANIFEST_VERSION,
    };
  }

  const analect = analectById.get(completion.quoteId);
  if (!analect) throw new Error(`unknown Analects quote: ${completion.quoteId}`);
  const expectedKey = learningResourceKey(completion.quoteId, completion.gate);
  if (completion.resourceKey !== expectedKey) throw new Error("learning resourceKey mismatch");

  const gate = gateById(completion.gate);
  const itemTitle = `${analect.title} · ${gate.label}`;
  const itemGroup = `論語 · ${analect.chapter}`;
  const score = completion.correct ? 100 : 0;
  const sourceUrl = dependencies.sourceUrl
    ?? (typeof window === "undefined" ? "https://ly.bdfz.net/" : `${window.location.origin}${window.location.pathname}`);
  const meta = {
    schemaVersion: 1,
    source: "lunyu-battle",
    manifestVersion: LEARNING_MANIFEST_VERSION,
    resourceKey: completion.resourceKey,
    completionKind: "answer_submitted",
    quoteId: completion.quoteId,
    quoteTitle: analect.title,
    chapter: analect.chapter,
    gate: completion.gate,
    gateLabel: gate.label,
    attemptNumber: completion.attemptNumber,
    submittedAt: completion.submittedAt,
    correctness: completion.correct,
    scorePercent: score,
  };
  const recordSourceCompletionImpl = dependencies.recordSourceCompletionImpl ?? recordSourceCompletion;
  await recordSourceCompletionImpl(completion);
  const syncProgressImpl = dependencies.syncProgressImpl ?? syncProgress;
  const recordEventImpl = dependencies.recordEventImpl ?? recordEvent;

  await syncProgressImpl({
    siteKey: SITE_KEY,
    itemKey: completion.resourceKey,
    itemTitle,
    itemGroup,
    itemType: "lunyu_gate_challenge",
    state: "completed",
    completed: true,
    score,
    progressPercent: 100,
    meta,
  });
  await recordEventImpl({
    siteKey: SITE_KEY,
    kind: "event",
    recordKey: `answer-submitted:${completion.resourceKey}:${completion.submittedAt}:${completion.attemptNumber}`.slice(0, 180),
    title: `完成 ${itemTitle}`,
    summary: completion.correct ? "已提交作答 · 正確" : "已提交作答 · 待鞏固",
    itemGroup,
    itemType: "lunyu_gate_completion",
    payload: { ...meta, sourceUrl },
  });

  return {
    status: "published",
    resourceKey: completion.resourceKey,
    manifestVersion: LEARNING_MANIFEST_VERSION,
  };
}

export type DeliveryStage = "source" | "progress" | "record";
export class DeliveryError extends Error {
  constructor(message: string, public retryable: boolean, public disposition: "queued" | "pending_mapping" | "quarantined" = "queued",
    public step?: string) {
    super(message);
  }
}
// The failing server step from publish.js, kept only as a short code.
const failureStep = (receipt: { step?: unknown }) =>
  typeof receipt.step === "string" && /^[a-z_]{1,24}$/.test(receipt.step) ? receipt.step : undefined;
/** Each receipt confirms one original event and one stage, never a whole batch. */
export async function publishCompletionStage(completion: LearningUnitCompletion, owner: string, stage: DeliveryStage): Promise<void> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 45000);
  try {
    const response = await fetch("/api/learning/publish", {
      method: "POST", credentials: "same-origin", cache: "no-store", signal: controller.signal,
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ completion, expectedOwner: owner, stage, manifestVersion: LEARNING_MANIFEST_VERSION }),
    });
    const receipt = await response.json();
    if (receipt.error === "session_changed" || response.status === 401) throw new DeliveryError("session_changed", false);
    if (["queued", "pending_mapping", "quarantined"].includes(receipt.disposition)) {
      throw new DeliveryError(receipt.error || receipt.disposition, receipt.retryable === true, receipt.disposition, failureStep(receipt));
    }
    if (!response.ok) throw new DeliveryError(receipt.error || "sync_unconfirmed", response.status === 429 || response.status >= 500,
      response.status >= 400 && response.status < 500 && response.status !== 429 ? "quarantined" : "queued");
    if (!response.ok || receipt.ok !== true || receipt.owner !== owner || receipt.stage !== stage
      || receipt.eventNonce !== completion.eventNonce || receipt.resourceKey !== completion.resourceKey
      || receipt.disposition !== "accepted") throw new DeliveryError("receipt_mismatch", false, "quarantined");
  } finally { clearTimeout(timer); }
}
